import React, { useEffect, useMemo, useState } from 'react';
import { Clock, X, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ImportHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  sharingOperationId?: string | null;
}

interface HistoryRow {
  monthKey: string;
  monthLabel: string;
  participantName: string;
  ean: string;
  volume_partage: number;
  volume_complementaire: number;
  injection_partagee: number;
  injection_complementaire: number;
  networkCosts?: {
    tarifOSP?: number;
    surcharges?: number;
    tarifMesure?: number;
    transportELIA?: number;
    redevanceVoirie?: number;
    tarifCapacitaire?: number;
    totalFraisReseau?: number;
    utilisationReseau?: number;
  };
  updatedAt?: string;
}

const fmt = (v: number) =>
  (v / 1000).toLocaleString('fr-BE', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

const fmtEuro = (v: number) =>
  Number(v || 0).toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

export function ImportHistoryModal({ isOpen, onClose, sharingOperationId }: ImportHistoryModalProps) {
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');
  const [filterParticipant, setFilterParticipant] = useState('all');

  const loadHistory = async () => {
    setLoading(true);
    try {
      let query = supabase.from('participants').select('name, ean_code, monthly_data, billing_data');
      if (sharingOperationId) {
        query = query.eq('sharing_operation_id', sharingOperationId);
      }
      const { data: participants, error } = await query;
      if (error) throw error;

      const history: HistoryRow[] = [];
      (participants || []).forEach((p: any) => {
        let md: Record<string, any> = {};
        let bd: Record<string, any> = {};
        try {
          if (p.monthly_data) md = typeof p.monthly_data === 'string' ? JSON.parse(p.monthly_data) : p.monthly_data;
          if (p.billing_data) bd = typeof p.billing_data === 'string' ? JSON.parse(p.billing_data) : p.billing_data;
        } catch {
          return;
        }
        Object.entries(md).forEach(([monthKey, data]: [string, any]) => {
          const billing = bd[monthKey] || {};
          history.push({
            monthKey,
            monthLabel: format(new Date(monthKey + '-01'), 'MMMM yyyy', { locale: fr }),
            participantName: p.name || 'Inconnu',
            ean: p.ean_code || '',
            volume_partage: Number((data as any)?.volume_partage || 0),
            volume_complementaire: Number((data as any)?.volume_complementaire || 0),
            injection_partagee: Number((data as any)?.injection_partagee || 0),
            injection_complementaire: Number((data as any)?.injection_complementaire || 0),
            networkCosts: billing.networkCosts,
            updatedAt: (data as any)?.updated_at || billing.updated_at,
          });
        });
      });
      history.sort((a, b) => b.monthKey.localeCompare(a.monthKey) || a.participantName.localeCompare(b.participantName));
      setRows(history);
    } catch {
      toast.error('Erreur lors du chargement de l\'historique');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) loadHistory();
  }, [isOpen, sharingOperationId]);

  const participantsList = useMemo(() => {
    const names = new Map<string, string>();
    rows.forEach(r => names.set(r.participantName, r.participantName));
    return Array.from(names.values()).sort();
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter(r => {
      if (filterStart && r.monthKey < filterStart) return false;
      if (filterEnd && r.monthKey > filterEnd) return false;
      if (filterParticipant !== 'all' && r.participantName !== filterParticipant) return false;
      return true;
    });
  }, [rows, filterStart, filterEnd, filterParticipant]);

  const filteredTotals = useMemo(() => ({
    volume_partage: filteredRows.reduce((s, r) => s + r.volume_partage, 0),
    volume_complementaire: filteredRows.reduce((s, r) => s + r.volume_complementaire, 0),
    injection_partagee: filteredRows.reduce((s, r) => s + r.injection_partagee, 0),
    injection_complementaire: filteredRows.reduce((s, r) => s + r.injection_complementaire, 0),
    fraisReseau: filteredRows.reduce((s, r) => s + Number(r.networkCosts?.totalFraisReseau || 0), 0),
  }), [filteredRows]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-amber-500" />
            <h3 className="text-lg font-semibold text-neutral-900">Historique des données importées</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-neutral-200 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-neutral-500">Du</label>
            <input
              type="month"
              value={filterStart}
              onChange={e => setFilterStart(e.target.value)}
              className="border border-neutral-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-neutral-500">Au</label>
            <input
              type="month"
              value={filterEnd}
              onChange={e => setFilterEnd(e.target.value)}
              className="border border-neutral-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          <select
            value={filterParticipant}
            onChange={e => setFilterParticipant(e.target.value)}
            className="border border-neutral-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          >
            <option value="all">Tous les participants</option>
            {participantsList.map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <button
            onClick={loadHistory}
            className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>

        <div className="overflow-auto flex-1">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 sticky top-0">
              <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                <th className="px-6 py-3 font-medium">Mois</th>
                <th className="px-4 py-3 font-medium">Participant</th>
                <th className="px-4 py-3 font-medium">EAN</th>
                <th className="px-4 py-3 font-medium text-right">Conso. partagée (MWh)</th>
                <th className="px-4 py-3 font-medium text-right">Conso. réseau (MWh)</th>
                <th className="px-4 py-3 font-medium text-right">Inj. partagée (MWh)</th>
                <th className="px-4 py-3 font-medium text-right">Inj. réseau (MWh)</th>
                <th className="px-4 py-3 font-medium text-right">Frais réseau</th>
                <th className="px-4 py-3 font-medium">Importé le</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-neutral-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto mb-3" />
                    Chargement de l'historique...
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-neutral-500">
                    Aucune donnée importée pour cette période
                  </td>
                </tr>
              ) : (
                filteredRows.map((r, i) => (
                  <tr key={`${r.monthKey}-${r.participantName}-${i}`} className="hover:bg-neutral-50">
                    <td className="px-6 py-3 capitalize text-neutral-900 whitespace-nowrap">{r.monthLabel}</td>
                    <td className="px-4 py-3 text-neutral-800">{r.participantName}</td>
                    <td className="px-4 py-3 text-neutral-500">{r.ean}</td>
                    <td className="px-4 py-3 text-right text-amber-700 font-medium whitespace-nowrap">{fmt(r.volume_partage)}</td>
                    <td className="px-4 py-3 text-right text-neutral-700 whitespace-nowrap">{fmt(r.volume_complementaire)}</td>
                    <td className="px-4 py-3 text-right text-teal-700 font-medium whitespace-nowrap">{fmt(r.injection_partagee)}</td>
                    <td className="px-4 py-3 text-right text-neutral-700 whitespace-nowrap">{fmt(r.injection_complementaire)}</td>
                    <td className="px-4 py-3 text-right text-neutral-800 whitespace-nowrap">{fmtEuro(r.networkCosts?.totalFraisReseau || 0)}</td>
                    <td className="px-4 py-3 text-neutral-500 whitespace-nowrap text-xs">{r.updatedAt ? format(new Date(r.updatedAt), 'dd/MM/yyyy', { locale: fr }) : '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
            {filteredRows.length > 0 && (
              <tfoot className="bg-neutral-50 border-t border-neutral-200">
                <tr className="text-sm font-semibold">
                  <td className="px-6 py-3 text-neutral-900" colSpan={3}>Total ({filteredRows.length} lignes)</td>
                  <td className="px-4 py-3 text-right text-amber-700">{fmt(filteredTotals.volume_partage)}</td>
                  <td className="px-4 py-3 text-right text-neutral-700">{fmt(filteredTotals.volume_complementaire)}</td>
                  <td className="px-4 py-3 text-right text-teal-700">{fmt(filteredTotals.injection_partagee)}</td>
                  <td className="px-4 py-3 text-right text-neutral-700">{fmt(filteredTotals.injection_complementaire)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{fmtEuro(filteredTotals.fraisReseau)}</td>
                  <td className="px-4 py-3" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
